<template>
    <div v-if="allowed.read.displayTable" class="cedex-dependencies-root flex-root-row">
        <CedexDependenciesSidebar
            :viewMode="viewMode"
            :layoutType="layoutType"
            :selected="layoutValue"
            :items="layoutValues"
            @updateView="updateView"
            @showFilter="showFilter"
        />
        <div class="flex-root-column editor-box">
            <!-- Header -->
            <div class="editor-table dependencies" ref="dependenciesTable">
                <table class="vcs-table">
                    <tr>
                        <!-- <th class="vcs-table-header">#</th> -->
                        <th
                            v-for="columnId in headerColumnOrder"
                            :key="columnId"
                            v-show="layoutType !== columnId && visibleColumns[columnId]"
                            class="vcs-table-header vcs-table-header-large"
                            :class="(ownSortingColumn === columnId ? 'sorted' + (ownSortingDirection === 'asc' ? ' sorted-ascending' : ' sorted-descending' ) : '')"
                            :style="(allColumnsObject[columnId] && allColumnsObject[columnId].width ? 'width: ' + allColumnsObject[columnId].width + 'px;' : '')"
                            @click="changeSorting(columnId)"
                        >
                            <div class="vcs-table-header-title">{{ $t(allColumnsObject[columnId] ? allColumnsObject[columnId].title : '') }}{{
                                columnId.required ? ' *' : ''
                            }}</div>
                            <div
                                v-show="allColumnsObject[columnId] ? allColumnsObject[columnId].filter : false"
                                class="vcs-table-header-filter"
                                :class="{active: filterIsActive(columnId)}"
                                @click.stop="showFilter(columnId)"
                                :title="filterTitle(columnId)"
                            />
                        </th>
                    </tr>
                    <tr
                        v-if="dependencyData.length === 0 && layoutValue"
                        class="no-data">
                        <td :colspan="columns.length">-- {{ $t('no CEDEX configuration found') }} --<br/>
                            <div class="add-first-line" @click="addRow(-1, 'isoCode')">{{ $t('add data') }}</div>
                        </td>
                    </tr>
                    <tr
                        v-else-if="dependencyData.length === 0 && !layoutValue"
                        class="no-data">
                        <td :colspan="columns.length">-- {{ $t('no data loaded. Please select a primary filter.') }} --</td>
                    </tr>
                    <tr
                        v-for="(dependency, index) in paginatedDependencyData"
                        :key="dependency.id"
                    >
                        <!-- <td>{{index}}</td>-->
                        <CedexDependenciesSelectTableCell
                            v-if="dependency.rowSpan_isoCode && layoutType !== 'isoCode' && visibleColumns.isoCode"
                            :readonly="!allowed.write.columns.isoCode"
                            type="isoCode"
                            :value="dependency.isoCode"
                            :allValuesObject="isoCodes"
                            :allValuesArray="isoCodesArray"
                            :codeTexts="codeTexts"
                            :multiSelect="true"
                            :rowSpan="dependency.rowSpan_isoCode"
                            :showAddRow="true"
                            :showCopy="true"
                            :groupValuesMap="isoCodeToSurveyTypeName"

                            @changeSelection="changeSelection($event, index, 'isoCode')"
                            @addRow="addRow(index, 'isoCode')"
                            @copyBlock="copyBlock(index)"
                        />

                        <td
                            v-if="dependency.rowSpan_isoCode && layoutType === 'componentCode' && visibleColumns.componentDescription"
                            :rowSpan="dependency.rowSpan_isoCode"
                        >{{codeDescriptions.component[dependency.componentCode]}}</td>

                        <CedexDependenciesSelectTableCell
                            v-if="dependency.rowSpan_locationCode && visibleColumns.locationCode"
                            :readonly="!allowed.write.columns.locationCode"
                            type="locationCode"
                            :value="dependency.locationCode"
                            :allValuesObject="locationCodes"
                            :allValuesArray="locationCodesArray"
                            :codeTexts="codeTexts"
                            :multiSelect="true"
                            :rowSpan="dependency.rowSpan_locationCode"
                            :showAddRow="dependency.showPlus_locationCode"
                            :groupValuesMap="locationCodePrefixToViewTypeName"
                            :useLineTextEllipsis="true"

                            @changeSelection="changeSelection($event, index, 'locationCode')"
                            @addRow="addRow(index, 'locationCode')"
                        />

                        <td
                            v-if="dependency.rowSpan_locationCode && visibleColumns.locationDescription"
                            :rowSpan="dependency.rowSpan_locationCode"
                        >{{locationDescriptions(dependency.locationCode)}}</td>

                        <CedexDependenciesSelectTableCell
                            v-if="dependency.rowSpan_componentCode && layoutType !== 'componentCode' && visibleColumns.componentCode"
                            :readonly="!allowed.write.columns.componentCode"
                            type="componentCode"
                            :value="dependency.componentCode"
                            :allValuesObject="componentCodes"
                            :allValuesArray="componentCodesArray"
                            :codeTexts="codeTexts"
                            :rowSpan="dependency.rowSpan_componentCode"
                            :showAddRow="dependency.showPlus_componentCode"
                            :validationHint="validationErrors[index] && validationErrors[index].componentCode"

                            @changeSelection="changeSelection($event, index, 'componentCode')"
                            @addRow="addRow(index, 'componentCode')"
                        />

                        <td
                            v-if="dependency.rowSpan_componentCode && layoutType !== 'componentCode' && visibleColumns.componentDescription"
                            :rowSpan="dependency.rowSpan_componentCode"
                        >{{codeDescriptions.component[dependency.componentCode]}}</td>

                        <CedexDependenciesSelectTableCell
                            v-if="dependency.rowSpan_materialCode && visibleColumns.materialCode"
                            :readonly="!allowed.write.columns.materialCode"
                            type="materialCode"
                            :value="dependency.materialCode"
                            :allValuesObject="materialCodes"
                            :allValuesArray="materialCodesArray"
                            :codeTexts="codeTexts"
                            :rowSpan="dependency.rowSpan_materialCode"
                            :showAddRow="dependency.showPlus_materialCode"
                            :validationHint="validationErrors[index] && validationErrors[index].materialCode"

                            @changeSelection="changeSelection($event, index, 'materialCode')"
                            @addRow="addRow(index, 'materialCode')"
                        />

                        <td
                            v-if="dependency.rowSpan_materialCode && visibleColumns.materialDescription"
                            :rowSpan="dependency.rowSpan_materialCode"
                        >{{codeDescriptions.material[dependency.materialCode]}}</td>

                        <CedexDependenciesSelectTableCell
                            v-if="dependency.rowSpan_damageCode && visibleColumns.damageCode"
                            :readonly="!allowed.write.columns.damageCode"
                            type="damageCode"
                            :value="dependency.damageCode"
                            :allValuesObject="damageCodes"
                            :allValuesArray="damageCodesArray"
                            :codeTexts="codeTexts"
                            :rowSpan="dependency.rowSpan_damageCode"
                            :showAddRow="dependency.showPlus_damageCode"
                            :validationHint="validationErrors[index] && validationErrors[index].damageCode"

                            @changeSelection="changeSelection($event, index, 'damageCode')"
                            @addRow="addRow(index, 'damageCode')"
                        />

                        <td
                            v-if="dependency.rowSpan_damageCode && visibleColumns.damageDescription"
                            :rowSpan="dependency.rowSpan_damageCode"
                        >{{codeDescriptions.damage[dependency.damageCode]}}</td>

                        <CedexDependenciesSelectTableCell
                            v-if="visibleColumns.repairCode"
                            :readonly="!allowed.write.columns.repairCode"
                            type="repairCode"
                            :value="dependency.repairCode"
                            :allValuesObject="repairCodes"
                            :allValuesArray="repairCodesArray"
                            :codeTexts="codeTexts"
                            :showAddRow="dependency.showPlus_repairCode"
                            :validationHint="validationErrors[index] && validationErrors[index].repairCode"

                            @changeSelection="changeSelection($event, index, 'repairCode')"
                            @addRow="addRow(index, 'repairCode')"
                        />

                        <td
                            v-if="visibleColumns.repairDescription"
                        >{{codeDescriptions.repair[dependency.repairCode]}}</td>

                        <CedexDependenciesSelectTableCell
                            v-if="visibleColumns.repairArea"
                            :readonly="!allowed.write.columns.repairArea"
                            type="repairArea"
                            :value="dependency.repairArea"
                            :allValuesObject="repairAreas"
                            :allValuesArray="repairAreasArray"
                            :codeTexts="false"
                            :multiSelect="true"
                            :showAllValuesAsValues="true"

                            @changeSelection="changeSelection($event, index, 'repairArea')"
                        />

                        <CedexDependenciesSelectTableCell
                            v-if="visibleColumns.inspectionCriteria"
                            :readonly="!allowed.write.columns.inspectionCriteria"
                            type="inspectionCriteria"
                            :value="dependency.inspectionCriteria"
                            :allValuesObject="inspectionCriterias"
                            :allValuesArray="inspectionCriteriasArray"
                            :codeTexts="false"
                            :multiSelect="true"
                            :showAllValuesAsValues="true"

                            @changeSelection="changeSelection($event, index, 'inspectionCriteria')"
                        />

                        <td
                            v-if="visibleColumns.contentParameter"
                            class="contentParameter">
                            <img class="contentParameterImage" src="@/assets/icons/contentParameters/replacement.svg"
                                 :class="(dependency.replacement ? 'active' : 'inactive')"
                                 @click="openPopup('replacement')" title="Show CEDEX Replacements" alt="">
                            <img class="contentParameterImage" src="@/assets/icons/contentParameters/amendment.svg"
                                 :class="(dependency.amendment   ? 'active' : 'inactive')"
                                 @click="openPopup('amendment')" title="Show Checkline Amendments" alt="">
                            <img class="contentParameterImage" src="@/assets/icons/contentParameters/tag.svg"
                                 :class="(dependency.tag         ? 'active' : 'inactive')" @click="openPopup('tag')"
                                 title="Show CEDEX Tags" alt="">
                            <img class="contentParameterImage" src="@/assets/icons/contentParameters/infoPopup.svg"
                                 :class="(dependency.infoPopup   ? 'active' : 'inactive')"
                                 @click="openPopup('infoPopup')" title="Show CEDEX Info Popups" alt="">
                            <img class="contentParameterImage" src="@/assets/icons/contentParameters/favorite.svg"
                                 :class="(dependency.favorite    ? 'active' : 'inactive')"
                                 @click="openPopup('favorite')" title="Show CEDEX Favorites" alt="">
                            <img class="contentParameterImage" src="@/assets/icons/contentParameters/comment.svg"
                                 :class="(dependency.comment     ? 'active' : 'inactive')" @click="openPopup('comment')"
                                 title="Show CEDEX Comments" alt="">
                        </td>
                        <td
                            v-if="visibleColumns.length"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.length}"
                              :src="require('@/assets/icons/check_box_' + (dependency.length === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('length', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.width"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.width}"
                              :src="require('@/assets/icons/check_box_' + (dependency.width === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('width', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.time"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.time}"
                              :src="require('@/assets/icons/check_box_' + (dependency.time === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('time', index)" alt=""></td>

                        <CedexDependenciesSelectTableCell
                            v-if="visibleColumns.laborScaling"
                            :readonly="!allowed.write.columns.laborScaling"
                            type="laborScaling"
                            :value="dependency.laborScaling"
                            :allValuesObject="scalings"
                            :allValuesArray="scalingsArray"
                            :codeTexts="true"

                            @changeSelection="changeSelection($event, index, 'laborScaling')"
                        />
                        <CedexDependenciesSelectTableCell
                            v-if="visibleColumns.materialScaling"
                            :readonly="!allowed.write.columns.materialScaling"
                            type="materialScaling"
                            :value="dependency.materialScaling"
                            :allValuesObject="scalings"
                            :allValuesArray="scalingsArray"
                            :codeTexts="true"

                            @changeSelection="changeSelection($event, index, 'materialScaling')"
                        />

                        <td
                            v-if="visibleColumns.quantityWeight"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.quantityWeight}"
                              :src="require('@/assets/icons/check_box_' + (dependency.quantityWeight === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('quantityWeight', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.csc"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.csc}"
                              :src="require('@/assets/icons/check_box_' + (dependency.csc === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('csc', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.dppExcluded"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.dppExcluded}"
                              :src="require('@/assets/icons/check_box_' + (dependency.dppExcluded === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('dppExcluded', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.iir"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.iir}"
                              :src="require('@/assets/icons/check_box_' + (dependency.iir === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('iir', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.combiRepair"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.combiRepair}"
                              :src="require('@/assets/icons/check_box_' + (dependency.combiRepair === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('combiRepair', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.reeferMechanic"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.reeferMechanic}"
                              :src="require('@/assets/icons/check_box_' + (dependency.reeferMechanic === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('reeferMechanic', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.materialList"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.materialList}"
                              :src="require('@/assets/icons/check_box_' + (dependency.materialList === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('materialList', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.singleLine"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.singleLine}"
                              :src="require('@/assets/icons/check_box_' + (dependency.singleLine === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('singleLine', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.flowAndTdbOnly"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.flowAndTdbOnly}"
                              :src="require('@/assets/icons/check_box_' + (dependency.flowAndTdbOnly === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('flowAndTdbOnly', index)" alt=""></td>

                        <td
                            v-if="visibleColumns.activeFlow"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.activeFlow}"
                              :src="require('@/assets/icons/check_box_' + (dependency.activeFlow === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('activeFlow', index)" alt=""></td>
                        <td
                            v-if="visibleColumns.activeExternal"
                        ><img class="boolean" :class="{readonly: !allowed.write.columns.activeExternal}"
                              :src="require('@/assets/icons/check_box_' + (dependency.activeExternal === '1' ? '' : 'un') + 'checked.svg')"
                              @click="handleBooleanChange('activeExternal', index)" alt=""></td>

                        <td v-if="visibleColumns.actions">
                            <img v-if="allowed.write.actions.delete" class="boolean delete" src="@/assets/icons/table/bin.svg" @click="removeRow(index)" alt="">
                            <DependencyComment
                                v-if="allowed.read.actions.comments"
                                :dependencyId="parseInt(dependency.id)"
                                :comments="(comments || {})[dependency.id] || {}"
                                :readonly="!allowed.write.actions.comments"
                                @showMessage="showMessage"
                                @updateComment="updateComment"
                            />
                        </td>

                        <td v-if="visibleColumns.history">
                            <History
                                :entity-id="parseInt(dependency.id)"
                                :history-preview="dependency.history"
                            />
                        </td>
                    </tr>
                </table>
            </div>
            <Pagination
                :pageCount="pageCount"
                :page-size="pageSize"
                :visible-buttons="7"
                v-model="currentPage"
                @input="setScrollTop"
                @pageSizeChange="setPaginationChange"
            />
            <div v-show="loading" class="loading"></div>
        </div>

        <TableFilters
            :key="layoutType + '_' + layoutValue"
            ref="dependencyFilters"
            :filters="filters"
            @change="onFiltersChange"
        />

        <CopyDialog
            v-if="copyIndex !== null"
            :isoCodeObject="isoCodes"
            :isoCodeArray="isoCodesArray"
            :isoCodeToSurveyTypeName="isoCodeToSurveyTypeName"
            :locationCodeObject="locationCodes"
            :locationCodeArray="locationCodesArray"
            :locationCodePrefixToViewTypeName="locationCodePrefixToViewTypeName"
            @change="insertCopiedLines"
            @cancel="copyIndex = null"
        />
    </div>
</template>

<script>
import CedexDependenciesSidebar from "./CedexDependenciesSidebar";
import CedexDependenciesSelectTableCell from "./CedexDependenciesSelectTableCell";
import Pagination from "../Table/Pagination.vue";
import i18n from '../../i18n';
import TableFilters from "../Table/Filter/TableFilters.vue"
import authorization from "../../plugins/authorization";
import DependencyComment from "./DependencyComment.vue";
import History from "../Table/History.vue";
import CopyDialog from "./CopyDialog.vue";

export default {
    name: "CedexDependencies",
    components: {
        DependencyComment,
        CedexDependenciesSelectTableCell,
        CedexDependenciesSidebar: CedexDependenciesSidebar,
        Pagination,
        TableFilters,
        History,
        CopyDialog
    },
    props: {
        // view data selection
        viewMode:     String,
        codeTexts:    Boolean,
        equipment:    String,
        serviceType:  String,
        layoutType:   String,
        layoutValue:  String,
        layoutValues: Object,
        columns:      Array, // numeric index array with all columnData (0 => {id, title, visible, width, filter, required})
        isoCodeToSurveyTypeName:          Object,
        locationCodePrefixToViewTypeName: Object,

        // dependency data
        dependencies: Object,
        sortingColumn: {
            type:    String,
            default: null
        },
        sortingDirection: {
            type:    String,
            default: null
        },

        // codes and types
        componentCodes:      Object,
        isoCodes:            Object,
        locationCodes:       Object,
        materialCodes:       Object,
        damageCodes:         Object,
        repairCodes:         Object,
        inspectionCriterias: Object,
        repairAreas:         Object,
        scalings:            Object,
        codeDescriptions:    Object,
        comments:            Object,
    },
    data: function () {
        return {
            dependencyData:           [],
            loading:                  false,
            // Pagination
            paginationVisibleButtons: 7,
            currentPage:              1,
            pageSize:                 100,
            // Sorting
            ownSortingColumn:         this.sortingColumn,
            ownSortingDirection:      this.sortingDirection,

            icon:                     'mdi-checkbox-blank-outline',
            selectValues:             {},
            removedRows:              [],

            activeFilterValues:       {},
            activeFilter:             false,
            availableFilterValues:    {},

            validationErrors:         {},

            copyIndex:                null,

            ignoredColumns: [
                'replacement',
                'amendment',
                'tag',
                'infoPopup',
                'favorite',
                'comment',
            ],

            allowed: {
                read:  {
                    actions: {},
                    columns: {}
                },
                write: {
                    actions: {},
                    columns: {}
                },
            },
        }
    },
    mounted() {
        let allowed = {
            read:  {
                actions: {},
                columns: {}
            },
            write: {
                actions: {},
                columns: {}
            },
        }

        let aclRoot  = 'Settings/CEDEX Settings/Dependencies/Editor';

        let allColumnsRead  = authorization.isAllowed(aclRoot + '/All Columns', authorization.read);
        let allColumnsWrite = authorization.isAllowed(aclRoot + '/All Columns', authorization.write);

        let displayTable = allColumnsRead || allColumnsWrite;

        for (let columnIndex in this.columns) {

            let column  = this.columns[columnIndex];
            if (column.title === 'Actions') {
                continue;
            }
            let aclPath = aclRoot + '/Columns/' + column.title;

            let read  = allColumnsRead  || authorization.isAllowed(aclPath, authorization.read);
            let write = allColumnsWrite || authorization.isAllowed(aclPath, authorization.write);
            allowed.read.columns[column.id]  = read;
            allowed.write.columns[column.id] = write;

            displayTable = displayTable || read || write;
        }

        allowed.read.displayTable = displayTable;

        let aclPathActions = aclRoot + '/Actions';
        allowed.write.actions.delete   = authorization.isAllowed(aclPathActions + '/Delete', authorization.write);
        allowed.read.actions.comments  = authorization.isAllowed(aclPathActions + '/Comments', authorization.read);
        allowed.write.actions.comments = authorization.isAllowed(aclPathActions + '/Comments', authorization.write);

        this.allowed = allowed;
    },
    watch: {
        dependencies: function () {
            this.clearValidationErrors();
            this.calculateDependencyData();
        },
        sortingColumn: function () {
            this.ownSortingColumn = this.sortingColumn;
        },
        sortingDirection: function () {
            this.ownSortingDirection = this.sortingDirection;
        }
    },
    methods: {
        setScrollTop() {
            this.$refs.dependenciesTable.scrollTo(0, 0)
        },
        setPaginationChange(pageSize) {
            this.pageSize = pageSize;
            if (this.currentPage > this.pageCount) {
                this.currentPage = this.pageCount;
            }
        },
        updateView(name, value) {
            this.$emit('updateView', name, value, 'dependencies')
        },
        changeSorting(columnId) {
            // every tree column is sorted ascending by default
            // so change sorting of one of these columns is only possible to descending
            // revert the descending sorting means the column is sorted ascending again
            // all columns after inspection criteria can be sorted ascending or
            if (this.ownSortingColumn === columnId) {
                if (this.ownSortingDirection === 'desc') {
                    this.ownSortingDirection = 'asc';
                } else {
                    this.ownSortingDirection = 'desc';
                }
            } else {
                this.ownSortingDirection = 'desc';
            }

            this.ownSortingColumn = columnId;

            this.$emit('updateView', 'sortingColumn', this.ownSortingColumn, this.ownSortingDirection);
        },
        showFilter(id) {
            this.$refs.dependencyFilters.activeFilter = id;
        },
        onFiltersChange(filterData, showResultGroup) {
            if (JSON.stringify(this.activeFilterValues) === JSON.stringify(filterData)) {
                return false;
            }

            Object.assign(this.activeFilterValues, filterData);
            this.$emit('filterDependencies', filterData, showResultGroup);
        },
        getRowSpanData(column, index) {
            if (column === 'repairCode') {
                return {
                    rowSpan: 1,
                    index:   index
                }
            }
            let currentIndex = index;
            let rowSpan = 1; // default value, can not be smaller
            while (currentIndex >= 0) {
                if (typeof this.dependencyData[currentIndex]['rowSpan_' + column] !== 'undefined') {
                    rowSpan = this.dependencyData[currentIndex]['rowSpan_' + column];
                    break;
                }
                currentIndex--;
            }

            return {
                rowSpan: rowSpan,
                index:   currentIndex
            }
        },
        isMultiSelectColumn(column) {
            return ['isoCode', 'locationCode', 'repairArea', 'inspectionCriteria'].indexOf(column) >= 0;
        },
        isBooleanColumn(column) {
            return [
                'length',
                'width',
                'time',
                'cost',
                'csc',
                'dppExcluded',
                'reeferMechanic',
                'materialList',
                'quantityWeight',
                'combiRepair',
                'iir',
                'singleLine',
                'flowAndTdbOnly',
                'activeFlow',
                'activeExternal',
            ].indexOf(column) >= 0;
        },
        isScalingColumn(column) {
            return ['laborScaling', 'materialScaling',].indexOf(column) >= 0;
        },
        changeSelection(selectValue, index, type) {
            // handles the change of a (multi) select column
            let value;
            if (this.isMultiSelectColumn(type)) {
                selectValue = selectValue.filter(function (el) {
                    return el !== '';
                });
                // get values or null for all values
                if (selectValue.length === this[type + 'sArray'].length) {
                    value = null;
                } else {
                    // remove empty values
                    value = selectValue.join(',');
                }
            } else {
                value = selectValue;
            }
            // clear possible validation error
            if (
                this.validationErrors[index]
                && this.validationErrors[index][type]
            ) {
                this.validationErrors[index][type] = false;
            }

            // change the dependency data for all sub rows
            let rowSpanData = {
                rowSpan: 1,
                index: index + this.paginationOffset
            };
            let columnOrderIndex = this.columnOrder.indexOf(type);
            if (
                columnOrderIndex < this.columnOrder.indexOf('repairCode')
                && columnOrderIndex >= 0
            ) {
                rowSpanData = this.getRowSpanData(type, index + this.paginationOffset);
            }

            for (let i = rowSpanData.index; i < rowSpanData.index + rowSpanData.rowSpan; i++) {
                this.dependencyData[i][type] = value;
            }
            window.showBeforeUnload = true;
        },
        handleBooleanChange(column, index) {
            if (!this.allowed.write.columns[column]) {
                return;
            }
            let value = this.dependencyData[index + this.paginationOffset][column];
            this.dependencyData[index + this.paginationOffset][column] = value === '1' ? '0' : '1';
            window.showBeforeUnload = true;
        },
        openPopup(type) {
            alert('open Popup for ' + type);
        },
        showLoading() {
            this.loading = true;
        },
        hideLoading() {
            this.loading = false;
        },
        convertCodes(type, codeInName) {
            let array = [];
            for (let code in this[type + 's']) {
                let element = {
                    id: code
                };
                if (this.codeTexts) {
                    element.name = (codeInName ? code + ' - ' : '') + this[type + 's'][code];
                } else {
                    element.name = (codeInName ? code : this[type + 's'][code]);
                }

                array.push(element);
            }

            return array;
        },
        updateRowSpanData(type, index, addedLineColumnType)
        {
            // the row span and show plus data will be updated, before a row is added or removed !!!

            /*
            * Example for better understanding
            *
            * Index | ISO Code | Location Code | Material Code | Damage Code | Repair Code
            * ------+----------+---------------+---------------+-------------+------------
            *  1    | 12G0  10 | IXXX        5 | SK          3 | BR        2 | RP       +
            *  2    |          |               |               |           + | IT       +
            *  3    |          |               |             + | DB       1+ | SC       +
            *  4    |          |               | AU          2 | BR        2 | RP       +
            *  5    |          |             + |             + |           + | IT       +
            *  6    |          | EXXX        5 | SK          3 | BR        2 | RP       +
            *  7    |          |               |               |           + | IT       +
            *  8    |          |               |             + | DB       1+ | SC       +
            *  9    |          |               | AU          2 | BR        2 | RP       +
            * 10    |        + |             + |             + |           + | IT       +
            * ------+----------+---------------+---------------+-------------+------------
            *
            * Remove Line 5
            * -> decrease row span of
            *    - Damage Code in line 4
            *    - Material Code in line 4
            *    - Location Code in line 1
            *    - ISO Code in line 1
            * -> set row plus
            *    - Damage Code in line 4
            *    - Material Code in line 4
            *
            * Algorithm
            * get row span data of all columns left from the inserted (parameter column) (going from right to left)
            * if index = row_span_index, ignore (will be removed with this line)
            * else decrease row span by one
            *
            * Add repair code line at line 8 (inserted as line 9)
            * -> increase row span of
            *    - Damage Code in line 8
            *    - Material Code in line 6
            *    - Location Code in line 6
            *    - ISO Code in line 1
            *
            * Algorithm
            * get row span data of all columns left from the inserted (parameter column) (going from right to left)
            * increase row span by one
            */
            let codeColumns = this.codeColumns.toReversed();
            if (type === 'remove') {
                codeColumns.forEach(((columnName) => {
                    if (columnName === 'repairCode') {
                        return true; // skip repair code, it has no row span
                    }
                    // recalculate row span
                    let columnRowSpan = this.getRowSpanData(columnName, index);
                    if (columnRowSpan.rowSpan > 1) {
                        if (columnRowSpan.index < index) {
                            // decrease parent line column row span
                            this.dependencyData[columnRowSpan.index]['rowSpan_' + columnName] = columnRowSpan.rowSpan - 1;
                        } else if (columnRowSpan.index === index) {
                            // if the current line is parent, set decreased row span for the next line
                            this.dependencyData[columnRowSpan.index + 1]['rowSpan_' + columnName] = columnRowSpan.rowSpan - 1;
                        }
                    } // else ignore lines with new empty span
                }));
            } else if (type === 'add') {

                // data will be updated before the line is inserted
                let typeColumnIndex = codeColumns.indexOf(addedLineColumnType);
                codeColumns.forEach(((columnName, columnIndex) => {
                    if (columnName === 'repairCode') {
                        return; // skip repair code, it has always row span 1
                    }
                    let columnRowSpan = this.getRowSpanData(columnName, index);
                    if (columnIndex > typeColumnIndex) {
                        // increase parent line column row span
                        this.dependencyData[columnRowSpan.index]['rowSpan_' + columnName] = columnRowSpan.rowSpan + 1;
                    }
                }));
            }
        },
        updateShowPlusData(type, index, addColumn)
        {
            let codeColumns = this.codeColumns.toReversed(); // starting with repair code, going left

            if (type === 'remove') {
                if (index === -1) {
                    return; // no data remaining, special case
                }
                codeColumns.forEach((columnName, columnIndex) => {
                    let parentColumn = codeColumns[columnIndex + 1];
                    let parentColumnRowSpanData = this.getRowSpanData(parentColumn, index);
                    // deleted plus buttons were already removed, add the new ones
                    if (index === parentColumnRowSpanData.index + parentColumnRowSpanData.rowSpan - 1) {
                        let setIndex = index;
                        if (columnName !== 'repairCode') {
                            let columnRowSpanData = this.getRowSpanData(columnName, index);
                            setIndex = columnRowSpanData.index;
                        }
                        this.dependencyData[setIndex]['showPlus_' + columnName] = true;
                    }
                });
            } else if (type === 'add') {
                // ignore plus on left of current column (they are set by row span)
                // ignore plus on the right, they are already set correct
                // only changed the current column:
                // set new line plus
                this.dependencyData[index]['showPlus_' + addColumn] = true;
                // remove above line plus
                let columnRowSpan = this.getRowSpanData(addColumn, index - 1);
                this.dependencyData[columnRowSpan.index]['showPlus_' + addColumn] = false;
            }
        },
        async removeRow(index) {

            if (!this.allowed.write.actions) {
                return;
            }

            const res = await this.$dialog.confirm({
                text: i18n.t('Do you really want to remove this dependency line?'),
                title: i18n.t('Delete CEDEX Dependency'),
                persistent: true,
                actions: {
                    false: i18n.t('No'),
                    true: i18n.t('Yes'),
                },
            });

            if (!res) {
                return;
            }

            index = index + this.paginationOffset;
            this.updateRowSpanData('remove', index);

            // remove the data
            this.removedRows.push(this.dependencyData[index].id);
            this.dependencyData.splice(index, 1);

            this.updateShowPlusData('remove', index - 1);

            window.showBeforeUnload = true;
        },
        addRow(index, type) {
            index = index + this.paginationOffset;
            // todo validate
            // check for already added rows with empty fields

            // copy line data
            let line = {};
            let insertIndex = 0;
            if (this.dependencyData.length === 0) {
                line = {
                    isoCode:                '',
                    locationCode:           '',
                    componentCode:          '',
                    materialCode:           '',
                    damageCode:             '',
                    repairCode:             '',
                    repairArea:             '',
                    inspectionCriteria:     '',
                    replacement:            false,
                    amendment:              false,
                    tag:                    false,
                    infoPopup:              false,
                    favorite:               false,
                    comment:                false,
                    length:                 '0',
                    width:                  '0',
                    time:                   '0',
                    laborScaling:           '1',
                    materialScaling:        '1',
                    quantityWeight:         '0',
                    csc:                    '0',
                    dppExcluded:            '0',
                    iir:                    '0',
                    combiRepair:            '0',
                    reeferMechanic:         '0',
                    materialList:           '0',
                    singleLine:             '0',
                    flowAndTdbOnly:         '0',
                    activeFlow:             '1',
                    activeExternal:         '1',
                    rowSpan_isoCode:        1,
                    rowSpan_locationCode:   1,
                    rowSpan_componentCode:  1,
                    rowSpan_materialCode:   1,
                    rowSpan_damageCode:     1,
                    showPlus_isoCode:       true,
                    showPlus_locationCode:  true,
                    showPlus_componentCode: true,
                    showPlus_materialCode:  true,
                    showPlus_damageCode:    true,
                    showPlus_repairCode:    true,
                };
            } else {
                Object.assign(line, this.dependencyData[index]);

                // disable all checkboxes
                line.length              = '0';
                line.width               = '0';
                line.time                = '0';
                line.laborScaling        = '0';
                line.materialScaling     = '0';
                line.quantityWeight      = '0';
                line.csc                 = '0';
                line.dppExcluded         = '0';
                line.iir                 = '0';
                line.combiRepair         = '0';
                line.reeferMechanic      = '0';
                line.materialList        = '0';
                line.singleLine          = '0';
                line.flowAndTdbOnly      = '0';
                line.activeFlow          = '1';
                line.activeExternal      = '1';

                // clear values and reset row action for all columns of the line type and to the right of it
                let codeColumns = this.codeColumns.toReversed();
                let typeColumnIndex = codeColumns.indexOf(type);
                codeColumns.forEach(((columnName, columnIndex) => {
                    if (columnIndex <= typeColumnIndex) {
                        // set row span to 1
                        line['rowSpan_' + columnName] = 1;
                        // reset line column value (due new line)
                        line[columnName] = '';
                    } else {
                        // remove existing row span data, will be set in the function updateRowSpanAndShowPlusData
                        delete line['rowSpan_' + columnName];
                    }
                }));

                // get line insert index (add new line at specific position if added by a row plus button)
                let rowSpanColumn = type; // if first column
                if (type !== this.codeColumns[0]) {
                    // if not first column, use column to the right as row span column
                    rowSpanColumn = this.codeColumns[this.codeColumns.indexOf(type) - 1];
                }
                let rowSpan = this.getRowSpanData(rowSpanColumn, index);
                insertIndex = rowSpan.index + rowSpan.rowSpan;
            }

            this.updateRowSpanData('add', index, type);

            line.id = Date.now();
            // add line to data, correct insert index with pagination data
            this.dependencyData.splice(insertIndex, 0, line);

            this.updateShowPlusData('add', insertIndex, type);

            // scroll into view and mark new line
            this.$nextTick(() => {
                let newLineElement = this.$el.querySelector('div.editor-table table tr:nth-child(' + (insertIndex - this.paginationOffset + 2) + ')');
                newLineElement.scrollIntoView({
                    block:    'end',
                    behavior: 'smooth'
                });
                newLineElement.classList.add('newLine');
            });

            window.showBeforeUnload = true;
        },
        async copyBlock(index)
        {
            const res = await this.$dialog.confirm({
                text:       i18n.t('Do you really want to copy this ISO code block?'),
                title:      i18n.t('Copy ISO code block'),
                persistent: true,
                actions: {
                    false: 'No',
                    true:  'Yes',
                },
            });

            if (!res) {
                this.copyIndex = null;
                return;
            }

            this.copyIndex = index + this.paginationOffset;
        },
        insertCopiedLines(isoCodes, locationCodes)
        {
            let rowSpanData = this.getRowSpanData('isoCode', this.copyIndex);
            let insertIndex = rowSpanData.index + rowSpanData.rowSpan;
            for (
                let i = rowSpanData.index;
                i < rowSpanData.index + rowSpanData.rowSpan;
                i++
            ) {
                let lineData = {...this.dependencyData[i]};
                lineData.id           = Date.now() + '00' + insertIndex;
                lineData.isoCode      = isoCodes;
                lineData.locationCode = locationCodes;

                this.dependencyData.splice(insertIndex, 0, lineData);
                insertIndex++;
            }

            this.copyIndex = null;

            window.showBeforeUnload = true;
        },
        collectData() {
            // workaround for missing data
            let dependencyData = [];
            let columnMap = {
                componentCode:      'c',
                isoCode:            'i',
                locationCode:       'l',
                materialCode:       'm',
                damageCode:         'd',
                repairCode:         'r',
                inspectionCriteria: 'n',
                length:             'e',
                width:              'w',
                time:               't',
                laborScaling:       'ls',
                materialScaling:    'ms',
                quantityWeight:     'q',
                repairArea:         'a',
                activeFlow:         'af',
                activeExternal:     'ae',
            };

            for (let index in this.dependencyData) {
                let rowData = {};
                for (let column in this.dependencyData[index]) {
                    if (
                        this.dependencyData[index][column] === null
                        || this.dependencyData[index][column] === ''
                        || column.indexOf('rowSpan') === 0
                        || column.indexOf('showPlus') === 0
                        || column.indexOf('history') === 0
                        || (
                            this.dependencyData[index][column] === '0'
                            && this.isBooleanColumn(column)
                        )
                        || this.ignoredColumns.includes(column)
                        || (
                            this.isScalingColumn(column)
                            && this.dependencyData[index][column] === '1'
                        )
                    ) {
                        continue;
                    }
                    let columnKey = columnMap[column] || column;

                    rowData[columnKey] = this.dependencyData[index][column];
                }
                dependencyData.push(rowData);
            }

            return {
                dependencyData: dependencyData,
                removedRows: this.removedRows
            };
        },
        setAvailableFilterValues(filters) {
            let availableFilterValues = {};
            Object.keys(filters).forEach((name) => {
                availableFilterValues[name] = filters[name];
            });
            this.availableFilterValues = availableFilterValues;
        },
        calculateDependencyData() {
            if (Object.keys(this.dependencies).length === 0) {
                this.dependencyData = [];
                this.setAvailableFilterValues({});

                return;
            }

            let data   = [];
            let levels = [
                'isoCode',
                'locationCode',
                'componentCode',
                'materialCode',
                'damageCode',
                'repairCode'
            ];

            /*
            * What should it do:
            *
            * iterate through the whole object structure
            * every first line of every column should get a xx_rowSpan = rowSpan
            * every other line should get a xx_display = false (or remove this cause it shold not display
            *
            * result should be an array of objects with the data of each line
            *
            */

            let globalIndex = 0;
            let maxLevelRowSpan = levels.indexOf('repairCode');
            let allRowSpans = {
                isoCode:       {},
                locationCode:  {},
                componentCode: {},
                materialCode:  {},
                damageCode:    {},
            };

            let availableFilters = {};
            let addFilterValues = (name, values) => {
                if (values === '' || values === null) {
                    values = [];
                } else {
                    values = values.split(',');
                }
                // array join and unique
                availableFilters[name] = (availableFilters[name] || []).concat(values) // join
                    .filter((v, i, a) => a.indexOf(v) === i); // unique
            };

            let formatDataRecursive = (levelData, level, parentLevelData) => {

                level = level || 0;
                parentLevelData = parentLevelData || {};

                if (typeof levelData.id === 'undefined') {
                    let counter = 0;
                    Object.keys(levelData).map((key) => {
                        // levelData is a json object with key as value and value as next sub level data
                        let value  = key;
                        let column = levels[level];

                        addFilterValues(column, value);
                        if (column === 'repairCode') {
                            // add other data
                            addFilterValues('repairArea',         levelData[key].repairArea);
                            addFilterValues('inspectionCriteria', levelData[key].inspectionCriteria);
                        }

                        if (
                            value === ''
                            && this.isMultiSelectColumn(column)
                        ) {
                            // null means all values for multi selects
                            // the data from server is given by empty strings because null can not be a key
                            // so reconvert the data to 'all values' for multi select fields
                            value = null;
                        }
                        parentLevelData[column] = value; // write value to level data
                        let globalIndexOfFirstOccurrenceOfLevel = globalIndex;
                        // go to next sub level and parse the data
                        let childLevelCounter = formatDataRecursive(levelData[key], level + 1, parentLevelData);
                        counter += childLevelCounter;

                        // add row span and show plus data
                        if (level < maxLevelRowSpan) {
                            data[globalIndexOfFirstOccurrenceOfLevel]['rowSpan_' + column] = childLevelCounter;
                            allRowSpans[column][globalIndexOfFirstOccurrenceOfLevel] = childLevelCounter;
                            // already set row plus for repair codes, due to easier algorithm
                            if (column === 'damageCode') {
                                data[globalIndexOfFirstOccurrenceOfLevel + childLevelCounter - 1]['showPlus_' + levels[level + 1]] = true;
                            }
                        }
                    });

                    return counter;
                } else {
                    let lineData = {
                        ...parentLevelData,
                        ...levelData
                    };

                    data.push(lineData);
                    globalIndex++;

                    return 1;
                }
            };

            formatDataRecursive(this.dependencies);

            this.setAvailableFilterValues(availableFilters);

            /*
                set code column plus buttons
                Algorithm:
                1. iterate through all code columns from right to left
                2. iterate through all row span entries from current column
                3. check row span data from previous column
                4. if sum of current row span start and length matches sum of previous
                   OR if no previous row span data exist (because first code column)
                   -> set plus at current row span start line
             */

            // get all code columns
            let codeColumns = this.codeColumns;

            // start from last one and got to first one
            codeColumns = codeColumns.toReversed();

            codeColumns.forEach((columnName, columnIndex) => {
                let columnRowSpans = allRowSpans[columnName];
                let prevColumnName = codeColumns[columnIndex + 1]; // next in array but previous
                let prevRowSpans   = allRowSpans[prevColumnName];
                if (typeof columnRowSpans === 'undefined') {
                    // set dummy values for repair code
                    columnRowSpans = {};
                    data.forEach((value, index) =>  {
                        columnRowSpans[index] = 1;
                    });
                }

                // iterate through every row span entry to check if current is the last one (-> set plus).
                Object.keys(columnRowSpans).forEach((currentRowSpanStartIndex) => {
                    // get row span data of current column
                    let currentRowSpanLength = columnRowSpans[currentRowSpanStartIndex];
                    let currentSum = parseInt(currentRowSpanStartIndex) + parseInt(currentRowSpanLength);

                    if (typeof prevRowSpans === "undefined") {
                        // if prev column data does not exist
                        // -> reached first column
                        // -> first column gets plus everytime
                        data[currentRowSpanStartIndex]['showPlus_' + columnName] = true;
                    } else {
                        // check state of previous column
                        for (let prevRowSpanStartIndex in prevRowSpans) {
                            // get row span data of prev column
                            let prevRowSpanLength = prevRowSpans[prevRowSpanStartIndex];
                            let prevSum = parseInt(prevRowSpanStartIndex) + parseInt(prevRowSpanLength);
                            // if row span sum (start + length) of current and previous column match
                            if (currentSum === prevSum) {
                                // add plus at current column start index
                                data[currentRowSpanStartIndex]['showPlus_' + columnName] = true;
                                break; // break current loop to start at next row span of current column
                            }
                        }
                    }
                });
            });

            this.dependencyData = data;
        },
        getColumn(columnId)
        {
            let column = this.columns.filter((column) => {
                return column.id === columnId;
            });
            if (column.length === 0) {
                return null;
            } else {
                return column[0];
            }
        },
        filterIsActive(columnId)
        {
            let column = this.getColumn(columnId);

            return (
                column
                && (
                    (
                        column.filter === 'bool'
                        && (
                            this.activeFilterValues[columnId] === true
                            || this.activeFilterValues[columnId] === false
                        )
                    ) || (
                        column.filter === 'data'
                        && (
                            Array.isArray(this.activeFilterValues[columnId])
                            && this.activeFilterValues[columnId].length > 0
                        )
                    ) || (
                        column.filter === 'string'
                        && (
                            typeof this.activeFilterValues[columnId] === 'string'
                            && this.activeFilterValues[columnId].trim() !== ''
                        )
                    )
                )
            );
        },
        filterTitle(columnId)
        {
            if (!this.filterIsActive(columnId)) {
                return '';
            }

            let column = this.getColumn(columnId);
            let output;
            if (column.filter === 'data') {
                let filterData = false;
                this.filters.forEach(iteratingFilter => {
                    if (iteratingFilter.name === columnId) {
                        filterData = iteratingFilter.data;
                    }
                });
                if (filterData === false) {
                    return '';
                }
                // extract filter names
                let texts = [];
                filterData.forEach(filter => {
                    if (this.activeFilterValues[columnId].includes(filter.id)) {
                        texts.push(filter.name);
                    }
                });
                // join names with max 10 results and a '+ x more' option

                if (texts.length > 10) {
                    output = texts.slice(0, 10).join('\r\n') + '\r\n+ ' + (texts.length - 10) + ' ' + i18n.t('more');
                } else {
                    output = texts.join('\r\n');
                }
            } else if (column.filter === 'bool') {
                output = i18n.t((this.activeFilterValues[columnId] ? '' : 'in') + 'active');
            } else if (column.filter === 'string') {
                output = i18n.t('contains') + ': ' + this.activeFilterValues[columnId];
            }

            return output;
        },
        locationDescriptions(locationCodesString) {
            if (typeof locationCodesString !== 'string') {
                return '';
            }
            let locationCodes = locationCodesString.split(',');
            let locationDescriptions = [];
            locationCodes.forEach((locationCode) => {
                if (this.codeDescriptions.location[locationCode]) {
                    locationDescriptions.push(this.codeDescriptions.location[locationCode]);
                }
            });

            // remove duplicates
            locationDescriptions.filter((value, index, array) => {return array.indexOf(value) === index});

            return locationDescriptions.join("\r\n");
        },
        showMessage(content, type, close, buttonText) {
            this.$emit('showMessage', content, type, close, buttonText);
        },
        updateComment(dependencyId, userId, userComment) {
            this.$emit('updateComment', dependencyId, userId, userComment);
        },


        clearValidationErrors() {
            // set after saving failed by parent component Dependencies.vue
            this.validationErrors = {};
        },
        setValidationErrors(validationErrors)
        {
            // set after saving failed by parent component Dependencies.vue
            this.clearValidationErrors();
            if (typeof validationErrors === 'object') {
                Object.keys(validationErrors).forEach((columnId) => {
                    validationErrors[columnId].forEach((line) => {
                        if (typeof this.validationErrors[line] === 'undefined') {
                            this.validationErrors[line] = {};
                        }
                        this.validationErrors[line][columnId] = true;
                    });
                });
            }
        },
    },
    computed: {
        visibleColumns() {
            let visibleColumns = [];
            for (let columnIndex in this.columns) {
                visibleColumns[this.columns[columnIndex].id] = this.columns[columnIndex].visible;
            }

            return visibleColumns;
        },
        headerColumnOrder() {
            let columnOrder = [];
            for (let columnIndex in this.columns) {
                if (this.layoutType !== this.columns[columnIndex].id) {
                    columnOrder.push(this.columns[columnIndex].id);
                }
            }
            // change order of component description if layout type is not component code
            // remove componentDescription
            let componentDescriptionIndex = columnOrder.indexOf('componentDescription');
            if (componentDescriptionIndex > 0) {
                columnOrder.splice(columnOrder.indexOf('componentDescription'), 1);
                if (this.layoutType === 'componentCode') {
                    columnOrder.splice(columnOrder.indexOf('isoCode') + 1, 0, 'componentDescription');
                } else {
                    columnOrder.splice(columnOrder.indexOf('componentCode') + 1, 0, 'componentDescription');
                }
            }

            return columnOrder;
        },
        allColumnsObject() {
            let allColumnsObject = {};
            this.columns.forEach((column) => {
                allColumnsObject[column.id] = column;
            });

            return allColumnsObject;
        },
        columnOrder() {
            let columnOrder = [];
            // isoCode - inspectionCriteria
            for (let columnIndex in this.columns) {
                if (this.layoutType !== this.columns[columnIndex].id) {
                    columnOrder.push(this.columns[columnIndex].id);
                    if (this.columns[columnIndex].id === 'inspectionCriteria') {
                        break;
                    }
                }
            }
            return columnOrder;
        },
        codeColumns() {
            // get all code columns
            let codeColumns = [];
            this.columnOrder.forEach((columnName) => {
                if (columnName.indexOf('Code') >= 0) {
                    codeColumns.push(columnName);
                }
            });

            return codeColumns;
        },
        pageCount() {
            return Math.ceil(this.dependencyData.length / this.pageSize);
        },
        paginationOffset() {
            return (this.currentPage - 1) * this.pageSize;
        },
        paginatedDependencyData() {
            if (this.dependencyData.length === 0) {
                return [];
            }
            let start = (this.currentPage - 1) * this.pageSize;
            let end = Math.min(this.currentPage * this.pageSize, this.dependencyData.length);
            let firstLine = Object.assign({}, this.dependencyData[start]);

            // correct row span for first line data, if page is greater than 1
            if (this.currentPage > 1) {
                for (let columnIndex = this.columnOrder.indexOf('damageCode'); columnIndex >= 0; columnIndex--) {
                    let columnName = this.columnOrder[columnIndex];
                    if (!firstLine['rowSpan_' + columnName]) {
                        let rowSpanData = this.getRowSpanData(columnName, start);
                        firstLine['rowSpan_' + columnName] = rowSpanData.rowSpan - (start - rowSpanData.index);
                    }
                }
            }

            let paginatedData = [firstLine];
            for (let i = start + 1; i < end; i++) {
                paginatedData.push(Object.assign({}, this.dependencyData[i]));
            }

            return paginatedData;
        },
        isoCodesArray() {
            return this.convertCodes('isoCode', true);
        },
        locationCodesArray() {
            return this.convertCodes('locationCode', true);
        },
        componentCodesArray() {
            return this.convertCodes('componentCode', true);
        },
        damageCodesArray() {
            return this.convertCodes('damageCode', true);
        },
        repairCodesArray() {
            return this.convertCodes('repairCode', true);
        },
        materialCodesArray() {
            return this.convertCodes('materialCode', true);
        },
        repairAreasArray() {
            return this.convertCodes('repairArea');
        },
        scalingsArray() {
            return this.convertCodes('scaling');
        },
        inspectionCriteriasArray() {
            return this.convertCodes('inspectionCriteria');
        },
        filters() {
            /*
             * Define filters here!
             *
             * Generates a filter object array.
             * Each filter has a name, title (display), type (data/input/...)
             * and if type = data a data array of selectable values.
             *
             * Example:
             * filters = [{
             *      name:  "isoCode"
             *      title: "ISO Code"
             *      type:  "data"
             *      data:  [{
             *          id:   "42G0"
             *          name: "42G0 - General Purpose Cont."
             *      }]
             * }]
             */
            let filters = [];
            for (let key in this.columns) {
                let column = this.columns[key];

                if (
                    !!column.filter
                    && this.layoutType !== column.id
                ) {
                    let filter = {
                        name:  column.id,
                        title: column.title,
                        type:  column.filter,

                    };

                    if (filter.type === 'data') {
                        let dataArrayName = column.id + 'sArray';
                        if (
                            column.id === 'laborScaling'
                            || column.id === 'materialScaling'
                        ) {
                            dataArrayName = 'scalingsArray';
                        }
                        filter.data = this[dataArrayName] ?? [];
                    } else if (filter.type === 'bool') {
                        filter.type = 'boolean';
                        filter.data = [
                            {id: 0, name: i18n.t('unset')},
                            {id: 1, name: i18n.t('active')},
                            {id: 2, name: i18n.t('inactive')},
                        ];
                    }

                    // restrict filters available data to the availableFilterValues of current column
                    // if this is null or empty, keep all data
                    let availableValues = this.availableFilterValues[column.id];
                    if (
                        availableValues
                        && availableValues.length > 0
                    ) {
                        // get intersect all array with read available values
                        let arrayData = [];
                        filter.data.forEach((entry) => {
                            if (availableValues.includes(entry.id)) {
                                arrayData.push(entry);
                            }
                        })

                        // set filter data
                        filter.data = arrayData;
                    }

                    filters.push(filter);
                }
            }

            return filters;
        }

    }
}
</script>

<style scoped>
div.editor-table.dependencies {
    background-image: url('../../assets/background_dependencies.svg');
}

div.editor-box table.vcs-table td:not(.contentParameter) {
    user-select: text;
    -moz-user-select: text;
    -webkit-user-select: text;
    -ms-user-select: text;
}

div.editor-box table.vcs-table td.validationHint {
    background-color: #effd5f;
    background-image: url('../../assets/icons/warning.svg');
    background-position: left bottom;
}

div.editor-box table.vcs-table td.isoCode,
div.editor-box table.vcs-table td.locationCode {
    white-space: pre-line;
}

div.editor-box table.vcs-table td.locationCode {
    font-family: D-DIN Condensed, monospace, sans-serif;
}

div.editor-box table.vcs-table td.contentParameter {
    white-space: nowrap;
}

div.editor-box table.vcs-table td img.contentParameterImage {
    width: 16px;
    height: 16px;
    margin: 0 8px;
    cursor: pointer;
}

div.editor-box table.vcs-table td img.contentParameterImage.inactive {
    opacity: 0.4;
    cursor: default;
}

div.editor-box table.vcs-table td img.contentParameterImage:first-child {
    margin-left: 0;
}

div.editor-box table.vcs-table td img.contentParameterImage:last-child {
    margin-right: 0;
}

div.editor-box table.vcs-table td img.rowIcon,
div.editor-box table.vcs-table td img.boolean,
div.editor-box div.editor-line img.icon {
    width: 16px;
    height: 16px;
    margin: 0 auto;
    display: block;
    cursor: pointer;
}

div.editor-box table.vcs-table td img.boolean.readonly {
    cursor: default;
}

</style>
